'use client';
import type { FunctionComponent } from 'react';

import { Button } from '@/ui-components/button/button';
import { SearchableDropdown } from '@/ui-components/dropdown/searchable-dropdown';
import { Flexbox } from '@/ui-components/flexbox/flexbox';
import { Input } from '@/ui-components/input/input';
import { Modal } from '@/ui-components/modal/modal';
import { Paragraph } from '@/ui-components/paragraph/paragraph';

import { useProjectModalParams } from './hooks/use-project-modal-params';

export const ProjectModal: FunctionComponent = () => {
  const {
    t,
    projectState,
    documentId,
    error,
    onSave,
    countriesOptions,
    customerOptions,
    customerName,
    countryName,
    isDisabled,
    isPending,
    addSearchParam,
    setError,
    setProjectState
  } = useProjectModalParams();

  const statusAddButton = (
    <Button
      variant='secondary'
      sizing='small'
      className='mx-auto mb-3 w-[90%]'
      onClick={() => {
        addSearchParam([{ key: 'modal', value: 'Customers' }]);
      }}
    >
      {t('T_MANAGE_CUSTOMERS')}
    </Button>
  );

  return (
    <Modal
      name='ProjectModal'
      title={{ text: documentId ? t('T_EDIT_PROJECT') : t('T_ADDING_PROJECT'), variant: 'h5' }}
      dismissible
      closeButton
    >
      <Flexbox direction='col' className='min-h-[270px] gap-3'>
        <Input
          label={t('T_PROJECT_NAME')}
          sizing='small'
          value={projectState.title}
          name='title'
          placeholder={t('T_ENTER_PROJECT_NAME')}
          onChange={(e) => {
            setProjectState({ ...projectState, title: e.target.value });
            setError({ message: '', details: '' });
          }}
          onKeyUp={(e) => (e.key === 'Enter' ? onSave() : undefined)}
          error={error.details === 'title' ? error.message : null}
          containerClassname='w-full'
        />
        <div>
          <Paragraph variant='text-sm'>{t('T_COUNTRY')}</Paragraph>
          <SearchableDropdown
            className='mt-2'
            name='country'
            sizing='small'
            options={countriesOptions}
            externalSearchTerm={countryName ?? ''}
            label={t('T_COUNTRY')}
            placeholder={t('T_SELECT_COUNTRY')}
            onSelect={(value) => {
              !Array.isArray(value) && setProjectState({ ...projectState, country: value });
              setError({ message: '', details: '' });
            }}
            onClear={() => {
              setProjectState({ ...projectState, country: '' });
              setError({ message: '', details: '' });
            }}
            error={error.details === 'country' ? error.message : null}
            inputBackground='light'
          />
        </div>
        <div>
          <Paragraph variant='text-sm'>{t('T_CUSTOMER')}</Paragraph>
          <SearchableDropdown
            sizing='small'
            name='customer'
            placeholder={t('T_SELECT_CUSTOMER')}
            options={customerOptions}
            externalSearchTerm={documentId ? customerName : ''}
            inputBackground='light'
            onSelect={(value) => !Array.isArray(value) && setProjectState({ ...projectState, customerId: value })}
            onClear={() => setProjectState({ ...projectState, customerId: '' })}
            bottomButton={statusAddButton}
          />
        </div>

        <Flexbox justify='end' className='mt-10'>
          <Button onClick={onSave} variant='primary' sizing='small' className='w-32' isLoading={isPending} disabled={isDisabled}>
            {documentId ? t('T_EDIT') : t('T_ADD')}
          </Button>
        </Flexbox>
      </Flexbox>
    </Modal>
  );
};
